<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <input
        v-model="filterText"
        type="text"
        placeholder="Filter lists…"
        class="px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">
      <button type="button" class="btn btn-primary btn-sm" @click="openCreate">
        <i class="far fa-plus mr-1" />
        New List
      </button>
    </div>

    <div v-if="loading" class="flex items-center gap-2 text-sm text-gray-500">
      <UiSpinner :size="4" screen-reader="Loading lists" />
      <span>Loading lists…</span>
    </div>

    <div v-else-if="visibleLists.length === 0"
      class="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
      <i class="far fa-folder-tree text-3xl text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
        <span v-if="filterText">No lists match "{{ filterText }}"</span>
        <span v-else>No lists yet for this scope</span>
      </p>
      <p class="text-sm text-gray-500 mt-1">
        <span v-if="filterText">Try a different filter, or create a new list.</span>
        <span v-else>Lists let you bundle related negative keywords together for reuse.</span>
      </p>
      <button v-if="!filterText" type="button" class="btn btn-primary btn-sm mt-3" @click="openCreate">
        <i class="far fa-plus mr-1" />
        Create your first list
      </button>
    </div>

    <ul v-else class="divide-y divide-gray-200 dark:divide-gray-700">
      <li v-for="list in visibleLists" :key="list.id" class="py-3 flex items-center gap-3">
        <button class="text-left flex-1" @click="toggleExpand(list.id)">
          <div class="text-sm font-medium text-primary-600 dark:text-primary-400">
            {{ list.name }}
          </div>
          <div class="text-xs text-gray-500">
            {{ list.keyword_count }} keywords · default {{ list.default_match_type }}
          </div>
        </button>
        <button class="text-sm text-gray-500 hover:text-gray-700"
          v-tooltip="'Edit list name, description, and default match type.'"
          @click="openEdit(list)">
          Edit
        </button>
        <button class="text-sm text-red-500 hover:text-red-700"
          v-tooltip="'Permanently delete this list and all of its keywords.'"
          @click="destroy(list)">
          Delete
        </button>
      </li>
    </ul>

    <section v-if="expanded" class="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-base font-semibold">
          {{ expandedList?.name }} — keywords
        </h3>
        <button class="text-sm text-gray-500" @click="expanded = null">
          Close
        </button>
      </div>
      <KeywordTable
        :key="expanded"
        :initial-keywords="expandedKeywords"
        :upsert-url="`/ajax/negative_keyword_lists/${expanded}/negative_keywords/bulk_upsert`"
        :delete-url-base="`/ajax/negative_keyword_lists/${expanded}/negative_keywords`"
        :csrf-token="csrfToken"
        :default-match-type="expandedList?.default_match_type || 'broad'" />
    </section>

    <UiSlidePanel ref="slidePanel" />

    <p v-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import UiSpinner from '~/javascript/components/ui/spinner.vue'
import { toastSuccess, toastError } from '~/javascript/utils/toast'
import { confirmAlert } from '~/javascript/utils/confirm_alert'
import KeywordTable from './keyword_table.vue'
import ListForm from './list_form.vue'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'

const props = defineProps({
  scope: { type: String, required: true }, // 'account' | 'business' | 'location'
  accountId: { type: String, default: null },
  businessId: { type: String, default: null },
  locationId: { type: String, default: null },
  csrfToken: { type: String, required: true }
})

const lists = ref([])
const filterText = ref('')
const loading = ref(false)
const error = ref(null)
const expanded = ref(null)
const expandedKeywords = ref([])
const slidePanel = ref(null)

const expandedList = computed(() => lists.value.find((l) => l.id === expanded.value))

const visibleLists = computed(() => {
  if (!filterText.value) return lists.value
  const f = filterText.value.toLowerCase()
  return lists.value.filter((l) => l.name.toLowerCase().includes(f))
})

const indexParams = () => {
  const p = new URLSearchParams({ scope: props.scope })
  if (props.businessId) p.append('business_id', props.businessId)
  if (props.locationId) p.append('location_id', props.locationId)
  return p.toString()
}

const fetchLists = async() => {
  loading.value = true
  error.value = null
  try {
    const res = await fetch(`/ajax/negative_keyword_lists?${indexParams()}`, { headers: { Accept: 'application/json' } })
    if (!res.ok) throw new Error(`Load failed: ${res.status}`)
    const data = await res.json()
    lists.value = data.lists
  } catch (e) {
    error.value = e.message
    toastError(e)
  } finally {
    loading.value = false
  }
}

const toggleExpand = async(listId) => {
  if (expanded.value === listId) {
    expanded.value = null
    return
  }
  // Fetch keywords BEFORE setting expanded so KeywordTable mounts with the data
  // already populated (the component's :key changes when expanded changes,
  // forcing a fresh mount that consumes initial-keywords just once).
  try {
    const res = await fetch(`/ajax/negative_keyword_lists/${listId}/negative_keywords`, {
      headers: { Accept: 'application/json' }
    })
    if (!res.ok) throw new Error(`Load failed: ${res.status}`)
    const data = await res.json()
    expandedKeywords.value = data.keywords || []
    expanded.value = listId
  } catch (e) {
    error.value = e.message
    toastError(e)
  }
}

const handleSaved = () => {
  slidePanel.value.close()
  fetchLists()
}

const openCreate = () => {
  slidePanel.value.open({
    component: ListForm,
    props: {
      csrfToken: props.csrfToken,
      createUrl: '/ajax/negative_keyword_lists',
      updateUrlBase: '/ajax/negative_keyword_lists',
      scope: props.scope,
      accountId: props.accountId,
      businessId: props.businessId,
      locationId: props.locationId
    },
    events: { saved: handleSaved }
  })
}

const openEdit = (list) => {
  slidePanel.value.open({
    component: ListForm,
    props: {
      list,
      csrfToken: props.csrfToken,
      createUrl: '/ajax/negative_keyword_lists',
      updateUrlBase: '/ajax/negative_keyword_lists',
      scope: props.scope,
      accountId: props.accountId,
      businessId: props.businessId,
      locationId: props.locationId
    },
    events: { saved: handleSaved }
  })
}

const destroy = async(list) => {
  const ok = await confirmAlert({
    title: `Delete "${list.name}"?`,
    text: `This list and all ${list.keyword_count} of its keyword${list.keyword_count === 1 ? '' : 's'} will be permanently removed. Any Locations that have this list applied will lose those negatives.`,
    confirmButtonText: 'Yes, delete'
  })
  if (!ok) return
  try {
    const res = await fetch(`/ajax/negative_keyword_lists/${list.id}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': props.csrfToken, Accept: 'application/json' }
    })
    if (!res.ok && res.status !== 204) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data.errors || ['Delete failed']).join(', '))
    }
    toastSuccess(`Deleted "${list.name}"`)
    fetchLists()
  } catch (e) {
    error.value = e.message
    toastError(e)
  }
}

onMounted(fetchLists)
</script>
